import {
  cloneData, cloneDataOrNull, cloneListOfCloneable, DataModelBuilder,
  isSameData, isSameList, SerializationContext, SerializedData,
} from '../../../dto/dto.js';
import { MapUtils } from '../../../../utility/map-utils.js';
import { JSONUtils } from '../../../../utility/json-utils.js';
import { LegacyPassCartItemDto } from './legacy-pass-cart-item.js';
import { OpenAccessPassTemplate } from '../../open-access-pass/open-access-pass-template.js';
import { CustomerProfile } from '../../customer-profile.js';
import { SeatingOptions } from '../../seating-options.js';
import { OpenAccessPassVariation } from '../../open-access-pass/open-access-pass-variation.js';
import { ComponentList } from '../../../../components/core/component-list.js';
import { VersionRange } from '../../../core/version-range.js';
import { Stage } from '../../open-access-pass/stage.js';

type SeatedAccessPassCartItemDtoCreateBaseArgs = {
  passId: string,
  viaProductId: string | null,
  viaGroupId: string | null,
  customer: CustomerProfile | null,
  seatingOptions: SeatingOptions | null,
  stages?: Stage[],
  template: OpenAccessPassTemplate,
  variation: OpenAccessPassVariation | null,
  components?: ComponentList,
};

type SeatedAccessPassCartItemDtoCreateWithCodeArgs = SeatedAccessPassCartItemDtoCreateBaseArgs & {
  code: string,
};

type SeatedAccessPassCartItemDtoCreateWithScanCodeArgs = SeatedAccessPassCartItemDtoCreateBaseArgs & {
  scanCode: string,
};

export class LegacySeatedAccessPassCartItemDto extends LegacyPassCartItemDto {
  static readonly TYPE_ID: string = 'f5d357d1beef44fbb889d360ca7197d7';
  static readonly SCHEMA_VERSION: number = 6;

  private constructor(
      public readonly passId: string,
      public readonly code: string,
      public readonly viaProductId: string | null,
      public readonly viaGroupId: string | null,
      public readonly customer: CustomerProfile | null,
      public readonly seatingOptions: SeatingOptions | null,
      public readonly stages: Stage[],
      public readonly template: OpenAccessPassTemplate,
      public readonly variation: OpenAccessPassVariation | null,
      public readonly components: ComponentList,
  ) {
    super();
  }

  get $typeId(): string {
    return LegacySeatedAccessPassCartItemDto.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    // Version should always be `exactly`.
    // Cart should never be backwards migrated, backend should be updated before frontend.
    return VersionRange.exactly(LegacySeatedAccessPassCartItemDto.SCHEMA_VERSION);
  }

  static create(args: SeatedAccessPassCartItemDtoCreateWithCodeArgs): LegacySeatedAccessPassCartItemDto;
  static create(args: SeatedAccessPassCartItemDtoCreateWithScanCodeArgs): LegacySeatedAccessPassCartItemDto;
  static create(args: SeatedAccessPassCartItemDtoCreateWithCodeArgs | SeatedAccessPassCartItemDtoCreateWithScanCodeArgs): LegacySeatedAccessPassCartItemDto {
    const code = 'code' in args ? args.code : args.scanCode;
    return new LegacySeatedAccessPassCartItemDto(
        args.passId,
        code,
        args.viaProductId,
        args.viaGroupId,
        args.customer,
        args.seatingOptions,
        args.stages ?? [],
        args.template,
        args.variation,
        args.components ?? new ComponentList(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacySeatedAccessPassCartItemDto {
    return new LegacySeatedAccessPassCartItemDto(
        MapUtils.getString(map, 'passId'),
        MapUtils.getString(map, 'code'),
        MapUtils.getStringOrNull(map, 'viaProductId'),
        MapUtils.getStringOrNull(map, 'viaGroupId'),
        context.fromDataOrNull(MapUtils.getRecord(map, 'customer')),
        context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'seatingOptions')),
        MapUtils.getList<Stage>(map, 'stages', (value) => context.fromData<Stage>(JSONUtils.decodeRecord(value))),
        context.fromData(MapUtils.getRecord(map, 'template')),
        context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'variation')),
        ComponentList.deserialize(context, MapUtils.get<SerializedData[]>(map, 'components')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'passId': this.passId,
      'code': this.code,
      'scanCode': this.code,
      'viaProductId': this.viaProductId,
      'viaGroupId': this.viaGroupId,
      'customer': context.toDataOrNull(this.customer),
      'seatingOptions': context.toDataOrNull(this.seatingOptions),
      'stages': this.stages.map((stage) => context.toData(stage)),
      'template': context.toData(this.template),
      'variation': context.toDataOrNull(this.variation),
      'components': this.components.serialize(context),
    };
  }

  clone(): this {
    return new LegacySeatedAccessPassCartItemDto(
        this.passId,
        this.code,
        this.viaProductId,
        this.viaGroupId,
        cloneDataOrNull(this.customer),
        cloneDataOrNull(this.seatingOptions),
        cloneListOfCloneable(this.stages),
        cloneData(this.template),
        cloneDataOrNull(this.variation),
        this.components.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.template, this.template) &&
      other.passId === this.passId &&
      other.code === this.code &&
      other.viaProductId === this.viaProductId &&
      other.viaGroupId === this.viaGroupId &&
      isSameData(other.variation, this.variation) &&
      isSameData(other.seatingOptions, this.seatingOptions) &&
      isSameList(other.stages, this.stages, isSameData) &&
      isSameData(other.customer, this.customer) &&
      isSameData(other.components, this.components);
  }

  static assertType(item: LegacyPassCartItemDto): item is LegacySeatedAccessPassCartItemDto {
    return item instanceof LegacySeatedAccessPassCartItemDto;
  }

  get scanCode(): string {
    return this.code;
  }
}

export class LegacySeatedAccessPassCartItemDtoBuilder extends DataModelBuilder<LegacySeatedAccessPassCartItemDto> {
  build(context: SerializationContext, map: SerializedData): LegacySeatedAccessPassCartItemDto {
    return LegacySeatedAccessPassCartItemDto.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(LegacySeatedAccessPassCartItemDto.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacySeatedAccessPassCartItemDto.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version parameter `components` of type `ComponentList` was introduced.
    if(version < 2) {
      version = 2;
      map['components'] = [];
    }

    // In this version parameter `scanCode` was renamed to `code`.
    if(version < 3) {
      map['code'] = MapUtils.getString(map, 'scanCode');
      version = 3;
    }

    // In this version parameter `viaProductId` of type `string | null` was added.
    if(version < 4) version = 4;

    // In this version parameter `viaGroupId` of type `string | null` was added.
    if(version < 5) version = 5;

    // In this version `seatingOptions` became nullable and staged seat assignments were added.
    if(version < 6) version = 6;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
